// components/grounded-answer-card-v2.js — DEEP MODE CARD v2
// ═══════════════════════════════════════════════════════════
// Evolution of GroundedAnswerCard. Same conceptual purpose (render
// a structured high-stakes answer with trust signals) but redesigned
// to feel like a WORK PRODUCT, not a chat artifact.
//
// Six core moves vs v1:
//   1. Inline numbered citations — claims and sources are one
//      continuous artifact, not detached
//   2. Promoted caveats — caveats are a labeled callout, not a
//      subtle italic line
//   3. Action footer — Re-verify / Challenge / Continue thread /
//      Pin / Copy / Export buttons
//   4. Confidence as a gauge — 5 dots filled by level, with a
//      caption explaining the basis
//   5. Section jumping — for long answers, a top-of-card TOC chip
//      row that scrolls to sections (only appears when content
//      earns it, > 300 words or has explicit sections)
//   6. What-if exploration — chip row at the bottom with variant
//      conditions the user can click to spawn a follow-up card
//
// Plus:
//   7. Living verification dot — small purple dot when context has
//      changed since the card was generated, hover for "re-verify?"
//
// All callbacks are optional. The card renders in read-only mode
// if no handlers are passed (good for preview pages).
// ═══════════════════════════════════════════════════════════

import { useState, useRef, memo } from 'react';
import MessageContent from './message-content';

// ── Color system (matches the rest of the workstation) ─────
const COLORS = {
  gold:       '#c8a96e',
  sage:       '#7a9e82',
  purple:     '#9b8fc4',
  terracotta: '#c46050',
  text1:      '#d4d4d4',
  text2:      '#a8a8a8',
  text3:      '#888',
  text4:      '#666',
  text5:      '#444',
  bg0:        '#07070a',
  bg1:        '#07070a',
  bg2:        '#07070a',
  bg3:        '#141414',
  border1:    '#1e1e2a',
  border2:    '#272736',
  border3:    '#2a2a2a',
};

// Confidence gauge fill levels
const CONFIDENCE_LEVELS = {
  high:   { dots: 5, color: COLORS.gold,   label: 'high' },
  medium: { dots: 3, color: COLORS.sage,   label: 'medium' },
  low:    { dots: 2, color: COLORS.purple, label: 'low' },
};

// ── Confidence gauge ───────────────────────────────────────
function ConfidenceGauge({ level, basis }) {
  const cfg = CONFIDENCE_LEVELS[level] || CONFIDENCE_LEVELS.medium;
  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end' }}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <div style={{ display: 'inline-flex', gap: 3 }}>
          {[1, 2, 3, 4, 5].map(i => (
            <span
              key={i}
              style={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: i <= cfg.dots ? cfg.color : 'transparent',
                border: `1px solid ${i <= cfg.dots ? cfg.color : COLORS.border3}`,
              }}
            />
          ))}
        </div>
        <span style={{
          fontSize: 9,
          color: cfg.color,
          fontFamily: 'Georgia, serif',
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
        }}>
          {cfg.label}
        </span>
      </div>
      {basis && (
        <span style={{
          fontSize: 9,
          color: COLORS.text4,
          fontFamily: 'Georgia, serif',
          fontStyle: 'italic',
          marginTop: 3,
        }}>
          {basis}
        </span>
      )}
    </div>
  );
}

// ── Living verification dot ────────────────────────────────
function ContextChangedDot() {
  const [hovered, setHovered] = useState(false);
  return (
    <span
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ position: 'relative', display: 'inline-flex' }}
    >
      <span style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: COLORS.purple,
        boxShadow: '0 0 8px rgba(155,143,196,0.6)',
        animation: 'gcv2-pulse 2.4s ease-in-out infinite',
      }} />
      {hovered && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 8px)',
          right: 0,
          background: COLORS.bg2,
          border: `1px solid ${COLORS.purple}55`,
          borderRadius: 5,
          padding: '8px 11px',
          minWidth: 200,
          fontSize: 10,
          color: COLORS.text2,
          fontFamily: 'Georgia, serif',
          lineHeight: 1.5,
          pointerEvents: 'none',
          zIndex: 30,
          boxShadow: '0 4px 14px rgba(0,0,0,0.6)',
        }}>
          Context has changed since this answer was generated. Re-verify?
        </div>
      )}
    </span>
  );
}

// ── Inline citation chip ───────────────────────────────────
function CitationChip({ num, source }) {
  const [hovered, setHovered] = useState(false);
  const hasUrl = source?.url && /^https?:\/\//.test(source.url);
  const Tag = hasUrl ? 'a' : 'span';
  const tagProps = hasUrl
    ? { href: source.url, target: '_blank', rel: 'noopener noreferrer' }
    : {};
  return (
    <span
      style={{ position: 'relative', display: 'inline-flex' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Tag
        {...tagProps}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 16,
          height: 14,
          padding: '0 4px',
          marginLeft: 2,
          marginRight: 1,
          fontSize: 9,
          fontFamily: 'Georgia, serif',
          fontWeight: 700,
          color: hasUrl ? COLORS.sage : COLORS.text3,
          background: hasUrl ? 'rgba(122,158,130,0.1)' : COLORS.bg3,
          border: `1px solid ${hasUrl ? 'rgba(122,158,130,0.4)' : COLORS.border3}`,
          borderRadius: 3,
          textDecoration: 'none',
          verticalAlign: 'super',
          lineHeight: 1,
          cursor: hasUrl ? 'pointer' : 'default',
        }}
      >
        {num}
      </Tag>
      {hovered && source && (
        <div style={{
          position: 'absolute',
          bottom: 'calc(100% + 6px)',
          left: 0,
          background: COLORS.bg2,
          border: `1px solid ${COLORS.border3}`,
          borderRadius: 5,
          padding: '8px 11px',
          minWidth: 220,
          maxWidth: 360,
          fontSize: 10,
          color: COLORS.text2,
          fontFamily: 'Georgia, serif',
          lineHeight: 1.55,
          pointerEvents: 'none',
          zIndex: 40,
          boxShadow: '0 4px 14px rgba(0,0,0,0.6)',
        }}>
          <div style={{
            fontSize: 9,
            color: hasUrl ? COLORS.sage : COLORS.text4,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            marginBottom: 4,
          }}>
            [{num}] {source.label}
          </div>
          {source.snippet && (
            <div style={{ fontStyle: 'italic' }}>{source.snippet}</div>
          )}
        </div>
      )}
    </span>
  );
}

// ── Inline text renderer with citation marker support ──────
// Splits text on [N] markers and renders each as a CitationChip.
// Mock content uses string format: "claim text [1] more text [2]".
function renderInlineText(text, sources = []) {
  if (!text) return null;
  const parts = text.split(/(\[\d+\])/g);
  return parts.map((part, i) => {
    const m = part.match(/^\[(\d+)\]$/);
    if (m) {
      const num = parseInt(m[1], 10);
      const source = sources[num - 1];
      return <CitationChip key={`c-${i}`} num={num} source={source} />;
    }
    return <span key={`t-${i}`}>{part}</span>;
  });
}

// ── Promoted caveat callout ────────────────────────────────
function CaveatCallout({ text }) {
  if (!text) return null;
  return (
    <div style={{
      margin: '14px 0',
      padding: '10px 14px',
      background: 'rgba(200,169,110,0.06)',
      border: '1px solid rgba(200,169,110,0.25)',
      borderLeft: `2px solid ${COLORS.gold}`,
      borderRadius: '4px 6px 6px 4px',
      display: 'flex',
      alignItems: 'flex-start',
      gap: 10,
    }}>
      <div style={{
        flexShrink: 0,
        width: 16,
        height: 16,
        borderRadius: '50%',
        background: COLORS.gold,
        color: COLORS.bg0,
        fontSize: 10,
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 1,
      }}>
        !
      </div>
      <div style={{ flex: 1 }}>
        <div style={{
          fontSize: 9,
          color: COLORS.gold,
          fontFamily: 'Georgia, serif',
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          marginBottom: 4,
        }}>
          Caveat
        </div>
        <div style={{
          fontSize: 12,
          color: COLORS.text1,
          fontFamily: 'Georgia, serif',
          lineHeight: 1.65,
        }}>
          {text}
        </div>
      </div>
    </div>
  );
}

// ── Sources list ───────────────────────────────────────────
function SourcesList({ sources }) {
  if (!sources || sources.length === 0) return null;
  return (
    <div style={{
      marginTop: 16,
      paddingTop: 12,
      borderTop: `1px solid ${COLORS.border1}`,
    }}>
      <div style={{
        fontSize: 9,
        color: COLORS.text4,
        fontFamily: 'Georgia, serif',
        fontWeight: 700,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        marginBottom: 8,
      }}>
        Sources
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {sources.map((s, i) => {
          const hasUrl = s?.url && /^https?:\/\//.test(s.url);
          return (
            <div key={i} style={{
              display: 'flex',
              gap: 8,
              fontSize: 11,
              fontFamily: 'Georgia, serif',
              lineHeight: 1.5,
            }}>
              <span style={{
                flexShrink: 0,
                color: hasUrl ? COLORS.sage : COLORS.text4,
                fontWeight: 700,
              }}>
                [{i + 1}]
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                {hasUrl ? (
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: COLORS.sage,
                      textDecoration: 'none',
                      fontWeight: 600,
                    }}
                  >
                    {s.label}
                  </a>
                ) : (
                  <span style={{ color: COLORS.text2, fontWeight: 600 }}>{s.label}</span>
                )}
                {s.snippet && (
                  <span style={{ color: COLORS.text4, fontStyle: 'italic' }}>
                    {' — '}{s.snippet}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── What-if exploration row ────────────────────────────────
function WhatIfExplorer({ items, onPick }) {
  const [hoveredIdx, setHoveredIdx] = useState(null);
  if (!items || items.length === 0) return null;
  return (
    <div style={{
      marginTop: 16,
      paddingTop: 12,
      borderTop: `1px solid ${COLORS.border1}`,
    }}>
      <div style={{
        fontSize: 9,
        color: COLORS.text4,
        fontFamily: 'Georgia, serif',
        fontWeight: 700,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        marginBottom: 8,
      }}>
        What changes if…
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {items.map((it, i) => {
          const isHovered = hoveredIdx === i;
          return (
            <button
              key={i}
              onClick={() => onPick?.(it)}
              onMouseEnter={() => setHoveredIdx(i)}
              onMouseLeave={() => setHoveredIdx(null)}
              style={{
                background: isHovered ? 'rgba(155,143,196,0.16)' : 'rgba(155,143,196,0.06)',
                border: `1px solid ${isHovered ? COLORS.purple : 'rgba(155,143,196,0.35)'}`,
                borderRadius: 16,
                color: COLORS.purple,
                fontSize: 11,
                fontFamily: 'Georgia, serif',
                padding: '4px 12px',
                cursor: 'pointer',
                transition: 'all 0.12s',
              }}
            >
              {it.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Section jumping (TOC chip row at top) ──────────────────
function SectionTOC({ sections, scrollContainerRef }) {
  if (!sections || sections.length < 2) return null;
  function jumpTo(anchor) {
    const el = scrollContainerRef?.current?.querySelector(`[data-section="${anchor}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  return (
    <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: 6,
      marginBottom: 14,
      paddingBottom: 12,
      borderBottom: `1px solid ${COLORS.border1}`,
    }}>
      {sections.map((s, i) => (
        <button
          key={i}
          onClick={() => jumpTo(s.anchor)}
          style={{
            background: 'transparent',
            border: `1px solid ${COLORS.border3}`,
            borderRadius: 4,
            color: COLORS.text3,
            fontSize: 10,
            fontFamily: 'Georgia, serif',
            padding: '3px 9px',
            cursor: 'pointer',
            transition: 'all 0.12s',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.borderColor = COLORS.gold;
            e.currentTarget.style.color = COLORS.gold;
          }}
          onMouseLeave={e => {
            e.currentTarget.style.borderColor = COLORS.border3;
            e.currentTarget.style.color = COLORS.text3;
          }}
        >
          {s.title}
        </button>
      ))}
    </div>
  );
}

// ── Action footer button ───────────────────────────────────
function ActionButton({ icon, label, accent, onClick, disabled, primary = false }) {
  const [hovered, setHovered] = useState(false);
  const bg = disabled ? 'transparent' : (hovered ? `${accent}1f` : 'transparent');
  const border = disabled ? COLORS.border1 : (hovered ? `${accent}88` : COLORS.border3);
  const color = disabled ? COLORS.text5 : (hovered ? accent : COLORS.text3);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: 5,
        color,
        fontSize: 10,
        fontFamily: 'Georgia, serif',
        letterSpacing: '0.04em',
        padding: '5px 11px',
        cursor: disabled ? 'default' : 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        transition: 'all 0.12s',
      }}
    >
      {icon && icon(color)}
      {label}
    </button>
  );
}

// ── Action footer ──────────────────────────────────────────
function ActionFooter({
  onReverify, onChallenge, onContinue, onPin, onCopy, onExport, onDelete, pinned,
  showReverify = true, showChallenge = true, showContinue = true, showExport = true,
  showDelete = false,
}) {
  // Two-click delete confirmation: first click → confirmDelete=true,
  // second click → fires onDelete. Auto-cancels after 3s if no second
  // click. Same pattern as the depth report card.
  const [confirmDelete, setConfirmDelete] = useState(false);

  function handleDeleteClick() {
    if (!onDelete) return;
    if (!confirmDelete) {
      setConfirmDelete(true);
      setTimeout(() => setConfirmDelete(false), 3000);
      return;
    }
    onDelete();
  }

  // Two sub-groups that wrap independently. Left = engagement actions
  // (Re-verify, Challenge, Continue thread). Right = save/share
  // actions (Pin, Copy, Export, Delete). When the column is narrow,
  // each group wraps internally instead of pushing siblings off-screen.
  return (
    <div style={{
      marginTop: 14,
      paddingTop: 12,
      borderTop: `1px solid ${COLORS.border1}`,
      display: 'flex',
      flexWrap: 'wrap',
      gap: 8,
      justifyContent: 'space-between',
    }}>
      {/* Left group — engagement */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {showReverify && (
          <ActionButton
            accent={COLORS.sage}
            label="Re-verify"
            onClick={onReverify}
            icon={(c) => (
              <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                <path d="M2 8a6 6 0 1 0 1.5-4M2 3v3h3" stroke={c} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          />
        )}
        {showChallenge && (
          <ActionButton
            accent={COLORS.purple}
            label="Challenge"
            onClick={onChallenge}
            icon={(c) => (
              <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                <path d="M8 2v6M8 12h.01" stroke={c} strokeWidth="1.6" strokeLinecap="round" />
                <circle cx="8" cy="8" r="6.5" stroke={c} strokeWidth="1.3" />
              </svg>
            )}
          />
        )}
        {showContinue && (
          <ActionButton
            accent={COLORS.gold}
            label="Continue"
            onClick={onContinue}
            icon={(c) => (
              <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                <path d="M3 3h6a4 4 0 0 1 0 8H6l-3 3v-3" stroke={c} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          />
        )}
      </div>

      {/* Right group — save/share */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        <ActionButton
          accent={COLORS.gold}
          label={pinned ? 'Pinned' : 'Pin'}
          onClick={onPin}
          disabled={pinned}
          icon={(c) => (
            <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
              <path d="M8 1v9M8 10l-3-3M8 10l3-3M3 13h10" stroke={c} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        />
        <ActionButton
          accent={COLORS.sage}
          label="Copy"
          onClick={onCopy}
          icon={(c) => (
            <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
              <rect x="4" y="4" width="9" height="10" rx="1" stroke={c} strokeWidth="1.3" />
              <path d="M3 11V3a1 1 0 0 1 1-1h7" stroke={c} strokeWidth="1.3" strokeLinecap="round" />
            </svg>
          )}
        />
        {showExport && (
          <ActionButton
            accent={COLORS.purple}
            label="Export"
            onClick={onExport}
            icon={(c) => (
              <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                <path d="M8 2v9M8 11l-3-3M8 11l3-3M3 14h10" stroke={c} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          />
        )}
        {showDelete && (
          <ActionButton
            accent={COLORS.terracotta}
            label={confirmDelete ? 'Click to confirm' : 'Delete'}
            onClick={handleDeleteClick}
            icon={(c) => (
              <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                <path d="M3 5h10M6 5V3h4v2M5 5l1 9h4l1-9" stroke={c} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          />
        )}
      </div>
    </div>
  );
}

// ── Provenance footer (bottom right) ───────────────────────
function ProvenanceFooter({ model, timestamp }) {
  return (
    <div style={{
      marginTop: 12,
      fontSize: 9,
      color: COLORS.text5,
      fontFamily: 'Georgia, serif',
      letterSpacing: '0.04em',
      textAlign: 'right',
    }}>
      {model || 'claude-opus-4-6'}
      {timestamp && ` · ${timestamp}`}
    </div>
  );
}

// ── Body renderer ──────────────────────────────────────────
// Two body modes:
//   1. paragraphs — structured array with inline [N] citations
//      (used by grounded answers from the model)
//   2. markdown_body — raw markdown string rendered via
//      MessageContent (used by lens findings and depth frames
//      where the content is already markdown prose)
function CardBody({ paragraphs, markdown_body, sources, scrollContainerRef }) {
  // Markdown mode — render via MessageContent
  if (markdown_body) {
    const fakeMessage = { role: 'assistant', content: markdown_body };
    return (
      <div ref={scrollContainerRef} style={{
        fontSize: 14,
        lineHeight: 1.75,
        color: COLORS.text1,
        fontFamily: 'Georgia, serif',
      }}>
        <MessageContent m={fakeMessage} i={0} />
      </div>
    );
  }

  // Structured paragraphs mode — supports section anchors + inline citations
  return (
    <div ref={scrollContainerRef} style={{
      fontSize: 14,
      lineHeight: 1.75,
      color: COLORS.text1,
      fontFamily: 'Georgia, serif',
    }}>
      {(paragraphs || []).map((p, i) => {
        // Section break marker
        if (p.heading) {
          return (
            <div
              key={i}
              data-section={p.anchor || `s-${i}`}
              style={{
                marginTop: i === 0 ? 0 : 18,
                marginBottom: 8,
                fontSize: 12,
                fontWeight: 700,
                color: COLORS.gold,
                fontFamily: 'Georgia, serif',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              {p.heading}
            </div>
          );
        }
        return (
          <p key={i} style={{
            margin: '0 0 12px 0',
          }}>
            {renderInlineText(p.text, sources)}
          </p>
        );
      })}
    </div>
  );
}

// ── Main card ──────────────────────────────────────────────
// One card, three callers (chat grounded answer, lens finding, depth
// frame). All props are optional except payload — when fields are
// missing, the corresponding section just doesn't render.
//
// Header customization (to make it work for all three uses):
//   header_label   — replaces "DEEP MODE" (e.g. "PATTERN", "FIRST PRINCIPLES")
//   header_color   — replaces gold (e.g. sage for gap, purple for cross-domain)
//   header_subtitle — small italic line under the label (frame one-liners)
//   header_icon    — replaces the deep-dot SVG (optional)
//
// Body customization:
//   paragraphs     — structured array with inline [N] citations (for grounded answers)
//   markdown_body  — raw markdown string (for lens findings and depth frames)
function GroundedAnswerCardV2({
  payload,
  pinned,
  onReverify,
  onChallenge,
  onContinue,
  onPin,
  onCopy,
  onExport,
  onDelete,
  onWhatIf,
  // Allow showing/hiding individual footer buttons per use case
  showReverify = true,
  showChallenge = true,
  showContinue = true,
  showExport = true,
  showDelete = false,
}) {
  const scrollContainerRef = useRef(null);

  if (!payload) return null;

  const {
    // Header
    header_label = 'Deep mode',
    header_color = COLORS.gold,
    header_subtitle,
    // Body
    paragraphs = [],
    markdown_body,
    // Trust signals
    confidence,
    confidence_basis,
    sources = [],
    caveats,
    // Layout
    sections,
    what_ifs,
    context_changed,
    // Provenance
    model_used,
    timestamp,
  } = payload;

  return (
    <div style={{
      width: '100%',
      // CSS min() so the card caps at 680 in wide containers (chat
      // panel) but shrinks to fit narrow containers (lens column in
      // the Excavate drawer ~280-400px wide). Same component, different
      // contexts, never overflows.
      maxWidth: 'min(680px, 100%)',
      boxSizing: 'border-box',
      background: COLORS.bg1,
      border: `1px solid ${COLORS.border2}`,
      borderLeft: `2px solid ${header_color}`,
      borderRadius: '4px 10px 10px 4px',
      padding: '16px 18px',
      position: 'relative',
    }}>
      <style jsx global>{`
        @keyframes gcv2-pulse {
          0%, 100% { opacity: 0.8; transform: scale(1); }
          50%      { opacity: 1; transform: scale(1.18); }
        }
      `}</style>

      {/* Header — label + optional subtitle + confidence + context dot */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 10,
        marginBottom: 14,
        paddingBottom: 12,
        borderBottom: `1px solid ${COLORS.border1}`,
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, paddingTop: 3 }}>
          <span style={{
            fontSize: 9,
            color: header_color,
            fontFamily: 'Georgia, serif',
            fontWeight: 700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}>
            <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="6" stroke={header_color} strokeWidth="1.4" />
              <circle cx="8" cy="8" r="2.5" fill={header_color} />
            </svg>
            {header_label}
          </span>
          {header_subtitle && (
            <span style={{
              fontSize: 10,
              color: COLORS.text3,
              fontFamily: 'Georgia, serif',
              fontStyle: 'italic',
              marginLeft: 17,
            }}>
              {header_subtitle}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {context_changed && <ContextChangedDot />}
          {confidence && <ConfidenceGauge level={confidence} basis={confidence_basis} />}
        </div>
      </div>

      {/* Section TOC — only when sections exist */}
      <SectionTOC sections={sections} scrollContainerRef={scrollContainerRef} />

      {/* Body — markdown OR structured paragraphs */}
      <CardBody
        paragraphs={paragraphs}
        markdown_body={markdown_body}
        sources={sources}
        scrollContainerRef={scrollContainerRef}
      />

      {/* Caveat callout — promoted, not subtle */}
      {caveats && <CaveatCallout text={caveats} />}

      {/* Sources list */}
      {sources && sources.length > 0 && <SourcesList sources={sources} />}

      {/* What-if exploration */}
      {what_ifs && what_ifs.length > 0 && (
        <WhatIfExplorer items={what_ifs} onPick={onWhatIf} />
      )}

      {/* Action footer */}
      <ActionFooter
        onReverify={onReverify}
        onChallenge={onChallenge}
        onContinue={onContinue}
        onPin={onPin}
        onCopy={onCopy}
        onExport={onExport}
        onDelete={onDelete}
        pinned={pinned}
        showReverify={showReverify}
        showChallenge={showChallenge}
        showContinue={showContinue}
        showExport={showExport}
        showDelete={showDelete}
      />

      {/* Provenance */}
      {(model_used || timestamp) && <ProvenanceFooter model={model_used} timestamp={timestamp} />}
    </div>
  );
}

export default memo(GroundedAnswerCardV2);
